// missionsquad-web/src/composables/useVoiceInput.ts
//
// Browser-local voice dictation via @missionsquad/browserai: press-to-toggle mic capture
// (MicRecorder) transcribed by an STT model running entirely in the browser (WebGPU when
// available, WASM otherwise — the SDK picks). The model downloads from huggingface.co on first
// use (~75 MB for moonshine-tiny) and is cached by the browser afterward — no backend call, no
// provider key, so it also works for unauthenticated public-chat visitors.
//
// When the local engine can't load or fails mid-take, signed-in users degrade to server-side
// transcription through the backend speech-processing relay (utils/speechProcessing.ts) — the
// upstream STT host/key are admin-managed and never reach the browser. Local stays primary;
// anonymous visitors keep the local-only behavior.
//
// Recording is deliberately SDK-free: the mic capture + WAV/PCM helpers are first-party
// (utils/micRecorder.ts), so the fallback chain still works when the browserai chunk itself
// fails to load — the SDK is only ever needed for *local* transcription.

import { reactive } from 'vue'
import type { BrowserAI } from '@missionsquad/browserai'
import { store } from '@/store'
import { MicRecorder, MicrophoneError } from '@/utils/micRecorder'
import { transcribeTakeServerSide } from '@/utils/speechProcessing'

/** English-only, ~75 MB download — ultra-low-latency STT tuned for short dictation clips. */
export const VOICE_STT_MODEL = 'onnx-community/moonshine-tiny-ONNX'

/** Takes shorter than this many 16 kHz samples (~¼ s) are discarded as accidental presses. */
const MIN_TAKE_SAMPLES = 4000

export type VoiceInputState = 'idle' | 'loading-model' | 'recording' | 'transcribing'

export interface VoiceInput {
  readonly state: VoiceInputState
  /** Model download progress, 0–1. Only meaningful while state is 'loading-model'. */
  readonly loadProgress: number
  /**
   * False when the mic button should be hidden: the browser can't record at all, or it can't
   * run the local engine (no WebAssembly) and the visitor is signed out so the server fallback
   * is unavailable too. Read live, so signing in can enable it.
   */
  readonly supported: boolean
  /** Which engine transcribed the last completed take — telemetry hook. Null until a take completes. */
  readonly engine: 'local' | 'server' | null
  /**
   * Press-to-toggle. Idle: loads the model if needed, then starts recording (resolves null);
   * when the model can't load and the user is signed in, recording starts anyway and the take
   * is routed server-side on stop. Recording: stops, transcribes (local first, server fallback
   * on a local failure when signed in), and resolves the transcript — or null when the take was
   * too short, silent, or empty. Presses while loading/transcribing are ignored (resolve null).
   * @throws {Error} with a user-presentable message (mic denied, model load failed with no
   *   fallback available, or every transcription path failed).
   */
  toggle(): Promise<string | null>
  /** Abandon any in-flight recording and release the mic (for component teardown). */
  cancel(): Promise<void>
}

type Sdk = typeof import('@missionsquad/browserai')

// Shared across composable instances so the SDK is fetched and the model is downloaded and
// initialized at most once per page. The dynamic import keeps the SDK (and the inference
// engines it dynamic-imports in turn) out of the app's initial bundle.
let sdkPromise: Promise<Sdk> | null = null
let client: BrowserAI | null = null
let modelLoadPromise: Promise<void> | null = null
let modelReady = false

function getSdk(): Promise<Sdk> {
  if (!sdkPromise) sdkPromise = import('@missionsquad/browserai')
  return sdkPromise
}

/**
 * Synchronous probe for the recording half of voice input: a secure context (getUserMedia
 * refuses plain HTTP), mic capture (getUserMedia + MediaRecorder), and Web Audio
 * (AudioContext + OfflineAudioContext — the recording is decoded/resampled to 16 kHz PCM with
 * them). Enough to record a take; transcribing it additionally needs the local engine
 * (isBrowserVoiceCompatible) or the signed-in server fallback.
 */
export function isBrowserMicCapable(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  if (!window.isSecureContext) return false
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') return false
  if (typeof MediaRecorder === 'undefined') return false
  if (typeof AudioContext === 'undefined' || typeof OfflineAudioContext === 'undefined') return false
  return true
}

/**
 * Probe for fully on-device voice input. Everything here must exist before we spend a ~75 MB
 * model download: mic capture (above) plus WebAssembly (the inference fallback when WebGPU is
 * absent — STT runs on either, so WebGPU itself is not required).
 */
export function isBrowserVoiceCompatible(): boolean {
  return isBrowserMicCapable() && typeof WebAssembly === 'object'
}

/**
 * Whether a take can be routed through the backend speech-processing relay. Read lazily at
 * press/stop time — not captured at composable creation — so sign-in state is always current.
 * The relay route requires auth, so anonymous public-chat visitors stay local-only.
 */
function canUseServerFallback(): boolean {
  return store.getters.authenticated === true
}

/**
 * Warm the voice model in the background (e.g. on dashboard entry) so the first mic press is
 * instant. Fire-and-forget: waits for a browser idle slot so it does not compete with page-load
 * work, applies the same device guards as the mic button, skips data-saver connections, and
 * swallows all failures — a failed preload just means the mic press loads with a spinner as usual.
 */
export function preloadVoiceModel(): void {
  if (!isBrowserVoiceCompatible()) return
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData) return // don't push a ~75 MB download at data-saver users
  const kickoff = () => {
    ensureModel(() => undefined).catch(() => undefined)
  }
  const win = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(kickoff, { timeout: 10000 })
  } else {
    window.setTimeout(kickoff, 3000)
  }
}

function ensureModel(onProgress: (fraction: number) => void): Promise<void> {
  if (!modelLoadPromise) {
    modelLoadPromise = (async () => {
      const sdk = await getSdk()
      if (!client) client = new sdk.BrowserAI()
      await client.load(VOICE_STT_MODEL, {
        onProgress: (info) => onProgress(info.progress)
      })
      modelReady = true
    })().catch((error) => {
      modelLoadPromise = null // a failed download must not poison every later attempt
      throw error
    })
  }
  return modelLoadPromise
}

export function useVoiceInput(): VoiceInput {
  let recorder: MicRecorder | null = null
  // Set when the local model was unavailable at record start — the take goes to the relay on stop.
  let serverBound = false

  const voice = reactive({
    state: 'idle' as VoiceInputState,
    loadProgress: 0,
    engine: null as 'local' | 'server' | null,

    // Getter so sign-in state is read live: browsers without WebAssembly can still record for
    // the server fallback, but only when signed in — anonymous visitors need the local engine.
    get supported(): boolean {
      return isBrowserVoiceCompatible() || (isBrowserMicCapable() && canUseServerFallback())
    },

    async toggle(): Promise<string | null> {
      if (voice.state === 'recording') return stopAndTranscribe()
      if (voice.state !== 'idle') return null // ignore presses while loading or transcribing
      return startRecording()
    },

    async cancel(): Promise<void> {
      await recorder?.cancel()
      if (voice.state === 'recording') voice.state = 'idle'
    }
  })

  async function startRecording(): Promise<null> {
    // The button is hidden when unsupported, but guard direct callers too — nothing (model
    // download included) may proceed on an incompatible browser.
    if (!voice.supported) {
      throw new Error('Voice input is not supported in this browser.')
    }
    voice.engine = null
    serverBound = false
    if (!modelReady) {
      voice.state = 'loading-model'
      try {
        await ensureModel((fraction) => {
          voice.loadProgress = fraction
        })
      } catch (error) {
        // Recording never needs the model or the SDK — signed-in users record anyway and the
        // take is transcribed through the relay on stop. Anonymous visitors keep today's error.
        if (!canUseServerFallback()) {
          voice.state = 'idle'
          const detail = error instanceof Error ? error.message : String(error)
          throw new Error(`Could not load the speech model: ${detail}`)
        }
        serverBound = true
      }
    }
    if (!recorder) recorder = new MicRecorder()
    try {
      const started = await recorder.start()
      voice.state = started ? 'recording' : 'idle'
      return null
    } catch (error) {
      voice.state = 'idle'
      if (error instanceof MicrophoneError && error.permissionDenied) {
        throw new Error('Microphone access was denied. Allow microphone access in your browser settings and try again.')
      }
      throw error instanceof Error ? error : new Error(String(error))
    }
  }

  async function stopAndTranscribe(): Promise<string | null> {
    voice.state = 'transcribing'
    try {
      let pcm: Float32Array | null | undefined
      try {
        pcm = await recorder?.stop()
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error)
        throw new Error(`Transcription failed: ${detail}`)
      }
      if (!pcm || pcm.length < MIN_TAKE_SAMPLES) return null

      if (!serverBound && modelReady && client) {
        try {
          const { text } = await client.transcribe(pcm)
          voice.engine = 'local'
          const trimmed = text.trim()
          // Whisper emits bracketed markers like "[BLANK_AUDIO]" for silence — treat those as no speech.
          if (!trimmed || /^\[.*\]$/.test(trimmed)) return null
          return trimmed
        } catch (error) {
          // Local → server: re-route this take through the relay when signed in; anonymous
          // visitors get the same error as before the fallback existed.
          if (!canUseServerFallback()) {
            const detail = error instanceof Error ? error.message : String(error)
            throw new Error(`Transcription failed: ${detail}`)
          }
        }
      }

      const text = await transcribeTakeServerSide(pcm)
      if (text === null) {
        // null means the relay request failed (the helper logs details); silence comes back as
        // '' — the end of the degradation chain is the one user-facing error.
        throw new Error('Transcription failed. Please try again.')
      }
      voice.engine = 'server'
      return text.length > 0 ? text : null
    } finally {
      voice.state = 'idle'
    }
  }

  return voice
}
